/**
 * Folder Selector Component
 *
 * Dropdown/autocomplete selector for choosing a folder.
 * Used in file upload and metadata editing forms.
 */

import React, { useEffect, useState } from 'react';
import { useFolderStore } from '../stores/folderStore';

interface FolderSelectorProps {
  value: string;
  onChange: (folderPath: string) => void;
  disabled?: boolean;
  className?: string;
  showInheritanceInfo?: boolean;
}

export const FolderSelector: React.FC<FolderSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  className = '',
  showInheritanceInfo = true,
}) => {
  const { folders, loadFolders } = useFolderStore();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Load folders on mount
  useEffect(() => {
    if (folders.length === 0) {
      loadFolders();
    }
  }, [loadFolders, folders.length]);

  // Find selected folder details
  const selectedFolder = folders.find(f => f.folder_path === value);

  // Filter folders based on search query
  const filteredFolders = folders.filter(folder =>
    folder.folder_path.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelect = (folderPath: string) => {
    onChange(folderPath);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setIsOpen(true);
  };

  return (
    <div className={`relative ${className}`}>
      {/* Input Field */}
      <div className="relative">
        <input
          type="text"
          value={searchQuery || value || ''}
          onChange={handleInputChange}
          onClick={handleInputClick}
          placeholder="Select or type folder path (e.g., /Presbyterian/Creeds)"
          className="w-full px-3 py-2 pr-10 bg-dark-bg border border-dark-border rounded-lg text-white placeholder-dark-text-muted focus:ring-2 focus:ring-accent-blue focus:border-accent-blue transition-colors"
          disabled={disabled}
        />
        <button
          type="button"
          onClick={handleInputClick}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-dark-text-muted hover:text-white transition-colors"
          disabled={disabled}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isOpen ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
          </svg>
        </button>
      </div>

      {/* Dropdown List */}
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-dark-card border border-dark-border rounded-lg shadow-xl max-h-60 overflow-y-auto">
          {/* Root folder option */}
          <div
            onClick={() => handleSelect('/')}
            className="px-3 py-2 cursor-pointer hover:bg-bridge-hover transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4 text-accent-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
            </svg>
            <span className="font-medium text-white">/</span>
            <span className="text-xs text-dark-text-muted">(Root - No inheritance)</span>
          </div>

          {/* Filtered folders */}
          {filteredFolders.length > 0 ? (
            filteredFolders.map(folder => (
              <div
                key={folder.id}
                onClick={() => handleSelect(folder.folder_path)}
                className={`
                  px-3 py-2 cursor-pointer hover:bg-bridge-hover transition-colors
                  ${value === folder.folder_path ? 'bg-accent-blue/10 border-l-2 border-accent-orange' : ''}
                `}
              >
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-accent-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                  </svg>
                  <span className="font-medium text-white">{folder.folder_path}</span>
                </div>
                {folder.denomination_id && (
                  <div className="ml-7 mt-1 flex gap-2 flex-wrap">
                    <span className="text-xs px-2 py-0.5 bg-accent-blue/20 text-accent-blue border border-accent-blue/30 rounded">
                      {folder.denomination_id}
                    </span>
                    {folder.document_type && (
                      <span className="text-xs px-2 py-0.5 bg-dark-text-muted/20 text-dark-text-muted border border-dark-text-muted/30 rounded">
                        {folder.document_type}
                      </span>
                    )}
                    {folder.is_official && (
                      <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 border border-green-500/30 rounded">
                        Official
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="px-3 py-2 text-dark-text-muted text-sm">
              No folders found
            </div>
          )}
        </div>
      )}

      {/* Inheritance Info */}
      {showInheritanceInfo && selectedFolder && selectedFolder.denomination_id && (
        <div className="mt-2 p-2 bg-accent-blue/10 border border-accent-blue/30 rounded-lg">
          <p className="text-xs text-white">
            <strong>Auto-inheritance:</strong> Files in this folder will automatically inherit{' '}
            <strong className="text-accent-blue">{selectedFolder.denomination_id}</strong>
            {selectedFolder.document_type && (
              <span> ({selectedFolder.document_type})</span>
            )}
            {selectedFolder.is_official && (
              <span>, marked as <strong>official</strong></span>
            )}.
          </p>
        </div>
      )}

      {/* No Inheritance Info */}
      {showInheritanceInfo && (!selectedFolder || !selectedFolder.denomination_id) && value && value !== '/' && (
        <div className="mt-2 p-2 bg-dark-bg border border-dark-border rounded-lg">
          <p className="text-xs text-dark-text-muted">
            <strong>No inheritance:</strong> This folder has no denomination assigned. Files will keep their own metadata.
          </p>
        </div>
      )}
    </div>
  );
};

export default FolderSelector;
